import { useState } from "react";
import db from "../db/database";
import {
    fetchInvoiceHistory,
    fetchFromBusinessCentral
} from "../utils/businessCentralUtils";
import {
    buildFilters,
    buildSyncMessage,
    mapInvoice,
    mapPostedInvoice,
    mapLine,
    mapPaymentDetails,
    mapLineCalculateVat,
    fetchLinesInChunks
} from "../utils/syncUtils";

export function useSyncHistoricos(handleHistoricosSynced) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const fetchAndMapInvoices = async (values) => {
        // No contabilizadas
        let filter = buildFilters(values, false);
        const invoiceHistory = await fetchInvoiceHistory(filter);

        // Contabilizadas
        filter = buildFilters(values, true);
        const postedInvoiceHistory = await fetchFromBusinessCentral(`PostedSalesInvoice?${filter}`);

        // Map
        const mappedInvoices = invoiceHistory.map(mapInvoice);
        const mappedPostedInvoices = postedInvoiceHistory.map((obj) => mapPostedInvoice(obj, "Invoice"));

        // Lines
        const allLines = [];
        const paymentLines = [];

        if (mappedInvoices.length > 0) {
            const invoiceLines = await fetchLinesInChunks("LinesSalesInvoice", mappedInvoices, mapLineCalculateVat);
            allLines.push(...invoiceLines);

            const invoicePayments = await fetchLinesInChunks(
                "DetallePagoFactura",
                mappedInvoices,
                mapPaymentDetails,
                35,
                "Document_No_"
            );
            paymentLines.push(...invoicePayments);
        }

        if (mappedPostedInvoices.length > 0) {
            const postedInvoiceLines = await fetchLinesInChunks("LinesPostedSalesInvoice", mappedPostedInvoices, mapLine);
            allLines.push(...postedInvoiceLines);

            const postedInvoicePayments = await fetchLinesInChunks(
                "DetallePagoFactura",
                mappedPostedInvoices,
                mapPaymentDetails,
                35,
                "Document_No_",
                true
            );
            paymentLines.push(...postedInvoicePayments);
        }

        return { mappedInvoices, mappedPostedInvoices, allLines, paymentLines };
    };

    const fetchAndMapCreditMemos = async (values) => {

        // No contabilizadas
        let filter = buildFilters(values, false);
        const creditMemoHistory = await fetchFromBusinessCentral(`SalesCreditMemo?${filter}`);

        // Contabilizadas
        filter = buildFilters(values, true);
        const postedCreditMemoHistory = await fetchFromBusinessCentral(`PostedSalesCreditMemo?${filter}`);

        // Map
        const mappedCreditMemos = creditMemoHistory.map(mapInvoice);
        const mappedPostedCreditMemos = postedCreditMemoHistory.map((obj) =>
            mapPostedInvoice(obj, "Credit Memo")
        );

        // Lines
        const allLines = [];
        const paymentLines = [];

        if (mappedCreditMemos.length > 0) {
            const creditMemoLines = await fetchLinesInChunks("LinesSalesCreditMemo", mappedCreditMemos, mapLine);
            allLines.push(...creditMemoLines);

            const creditMemoPayments = await fetchLinesInChunks(
                "DetallePagoFactura",
                mappedCreditMemos,
                mapPaymentDetails,
                35,
                "Document_No_"
            );
            paymentLines.push(...creditMemoPayments);
        }

        if (mappedPostedCreditMemos.length > 0) {
            const postedCreditMemoLines = await fetchLinesInChunks("LinesPostedSalesCreditMemo", mappedPostedCreditMemos, mapLineCalculateVat);
            allLines.push(...postedCreditMemoLines);

            const postedCreditMemoPayments = await fetchLinesInChunks(
                "DetallePagoFactura",
                mappedPostedCreditMemos,
                mapPaymentDetails,
                35,
                "Document_No_",
                true
            );
            paymentLines.push(...postedCreditMemoPayments);
        }

        return { mappedCreditMemos, mappedPostedCreditMemos, allLines, paymentLines };
    };


    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            // Fetch data
            const {
                mappedInvoices,
                mappedPostedInvoices,
                allLines: invoiceLines,
                paymentLines: invoicePayments,
            } = await fetchAndMapInvoices(values);

            const {
                mappedCreditMemos,
                mappedPostedCreditMemos,
                allLines: creditMemoLines,
                paymentLines: creditMemoPayments,
            } = await fetchAndMapCreditMemos(values);

            // Merge documents
            const allDocuments = [
                ...mappedInvoices,
                ...mappedCreditMemos,
                ...mappedPostedInvoices,
                ...mappedPostedCreditMemos,
            ];

            // Replace stored data
            await db.invoices.clear();
            await db.invoices.bulkAdd(allDocuments);

            const allLines = [...invoiceLines, ...creditMemoLines];
            const allPayments = [...invoicePayments, ...creditMemoPayments];

            await db.invoiceLines.clear();
            await db.invoiceLines.bulkAdd(allLines);

            await db.invoicePaymentLines.clear();
            await db.invoicePaymentLines.bulkAdd(allPayments);

            // Success message
            const invoiceCount = mappedInvoices.length;
            const creditMemoCount = mappedCreditMemos.length;
            const postedInvoiceCount = mappedPostedInvoices.length;
            const postedCreditMemosCount = mappedPostedCreditMemos.length;

            setSuccess(buildSyncMessage(invoiceCount, creditMemoCount, postedInvoiceCount, postedCreditMemosCount));

            if (handleHistoricosSynced) {
                await handleHistoricosSynced();
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };


    return { loading, setLoading, error, success, setError, setSuccess, handleSubmit };
}